import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from 'vue';
import { setGlobalLoading } from '@/composables/useAppLoading';
import { defaultUserRoute } from '@/utils/userRouting';
import LoginView from "../views/LoginView.vue";
import { useUserAuthStore } from "@/stores/userAuthStore";
import { useClientCompaniesStore } from "@/stores/clientCompaniesStore";
import { useViewModeStore } from "@/stores/viewModeStore";
import { useStorageSubscriptionStore } from "@/stores/storageSubscriptionStore";
import { useImpersonationService } from "@/services/impersonationService";
import { useAccountSwitchingService } from "@/services/accountSwitchingService";
import { logger } from "@/services/loggerService";
import { migratePermissions } from "@/constants/permissions";
import { BLOCKED_ROUTES, VYSUAL_ONLY_BLOCKED_ROUTES } from "@/constants/clientPermissions";
import { hasCompanyAccess, getPrimaryCompanyId } from "@/utils/companyAccess";
import { isAdminUser } from "@/utils/userUtils";
import { userService } from "@/services/userService";
import { projectService } from "@/services/projectService";
import { isReviewSetupEligible } from "@/utils/projectUtils";

// Per-route scroll positions for the inner <main> scroll container.
// Keyed by fullPath so revisits to the same route restore the most
// recent scroll. In-memory only — resets on hard refresh, which is the
// expected UX (window.scrollTo behavior matches).
const mainScrollPositions = new Map();

// Load a project doc for the review-wizard route guards. Returns null (never
// throws) so a bad id / read failure just falls through to normal behavior.
// Legacy ?tab= values → the company section routes that replaced them.
const COMPANY_TAB_ROUTES = {
  contracts: "admin-company-contracts",
  storage: "admin-company-storage",
  users: "admin-company-users",
  agency: "admin-company-agency",
  activity: "admin-company-activity",
};

const loadProjectForGuard = async (projectId) => {
  try {
    return await projectService.getProject(projectId);
  } catch (error) {
    logger.warn("[Router] Review guard failed to load project", { error: error.message });
    return null;
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      redirect: "/login"
    },
    {
      path: "/login",
      name: "login",
      component: LoginView,
      meta: {
        title: "Login to Vysual",
      },
    },
    {
      path: "/account-selector",
      name: "account-selector",
      component: () => import("@/views/AccountSelector.vue"),
      meta: {
        title: "Select Account | Vysual",
      },
    },
    {
      path: "/profile",
      name: "profile",
      component: () => import("@/views/ProfileView.vue"),
      meta: {
        title: "My Profile | Vysual",
      },
    },
    // Third-party app consent (OAuth). Signed-in only; the login redirect keeps
    // the full query string so the request survives sign-in.
    {
      path: "/oauth/authorize",
      name: "oauth-authorize",
      component: () => import("@/views/OAuthAuthorize.vue"),
      meta: {
        title: "Connect an app | Vysual",
      },
    },
    // Public routes (no authentication required)
    {
      path: "/view/share/:token",
      name: "public-share",
      component: () => import("@/views/public/PublicShareView.vue"),
      meta: {
        title: "Shared Assets | Vysual",
        public: true,
      },
    },
    {
      path: "/view/transfer/:token",
      name: "public-transfer",
      component: () => import("@/views/public/PublicTransferView.vue"),
      meta: {
        title: "File Transfer | Vysual",
        public: true,
      },
    },
    {
      path: "/view/review/:token",
      name: "public-review",
      component: () => import("@/views/public/PublicReviewView.vue"),
      meta: {
        title: "Review | Vysual",
        public: true,
      },
    },
    {
      path: "/demo",
      name: "demo",
      component: () => import("@/views/public/DemoView.vue"),
      meta: {
        title: "Demo | Vysual",
        public: true,
      },
    },

    {
      path: "/invite/:token",
      name: "invite-accept",
      component: () => import("@/views/public/InviteAcceptView.vue"),
      meta: {
        title: "Accept Invite | Vysual",
        public: true,
      },
    },
    {
      path: "/signup",
      name: "signup",
      component: () => import("@/views/public/SignupView.vue"),
      meta: {
        title: "Sign Up | Vysual",
        public: true,
      },
      beforeEnter: (_to, _from, next) => {
        const userAuthStore = useUserAuthStore();
        if (userAuthStore.user?.id) {
          next(userAuthStore.defaultRedirect);
          return;
        }
        next();
      },
    },

    // New accounts structure
    {
      path: "/accounts/:id",
      children: [
        {
          path: "",
          redirect: to => ({ name: "account-assets", params: to.params })
        },
        {
          path: "assets",
          name: "account-assets",
          component: () => import("@/views/client/CompanyAssets.vue"),
          meta: {
            title: "Assets | Vysual",
          },
        },
        {
          path: "assets/:assetId",
          name: "account-asset-detail",
          component: () => import("@/views/client/AssetDetail.vue"),
          meta: {
            title: "Asset Details | Vysual",
          },
          beforeEnter: async (to, _from, next) => {
            const storageSubscriptionStore = useStorageSubscriptionStore();
            try {
              await storageSubscriptionStore.loadSubscription(to.params.id, { force: true });
              if (storageSubscriptionStore.isSuspended(to.params.id)) {
                logger.warn('[Router] Asset detail blocked - account suspended', {
                  companyId: to.params.id
                });
                next({
                  name: 'account-assets',
                  params: { id: to.params.id },
                  query: { suspended: 'true' }
                });
                return;
              }
            } catch {
              // Don't block on error
            }
            next();
          },
        },
        {
          path: "projects",
          name: "account-projects",
          component: () => import("@/views/client/CompanyProjects.vue"),
          meta: {
            title: "Projects | Vysual",
          },
        },
        {
          path: "projects/:projectId",
          name: "account-project-details",
          component: () => import("@/views/client/ProjectDetails.vue"),
          meta: {
            title: "Project Details | Vysual",
          },
        },
        {
          path: "calendar",
          name: "account-calendar",
          component: () => import("@/views/client/CompanyCalendar.vue"),
          meta: {
            title: "Calendar | Vysual",
          },
        },
        {
          path: "company",
          name: "account-company-details",
          component: () => import("@/views/client/CompanyDetails.vue"),
          meta: {
            title: "Company Details | Vysual",
          },
        },
        {
          path: "upload",
          name: "account-upload",
          component: () => import("@/views/client/ClientUpload.vue"),
          meta: {
            title: "Upload | Vysual",
          },
        },
        {
          path: "sharing",
          name: "account-sharing",
          component: () => import("@/views/client/ClientSharing.vue"),
          meta: {
            title: "Sharing | Vysual",
          },
        },
        {
          path: "reviews",
          name: "account-reviews",
          component: () => import("@/views/client/ReviewDetail.vue"),
          meta: {
            title: "Reviews | Vysual",
          },
        },
        {
          path: "shows",
          name: "account-shows",
          component: () => import("@/views/client/ClientShows.vue"),
          meta: {
            title: "Podcasts | Vysual",
          },
        },
        {
          path: "shows/:showId",
          name: "account-show-detail",
          component: () => import("@/views/client/ClientShowDetail.vue"),
          meta: {
            title: "Show | Vysual",
          },
        },
        {
          path: "shows/:showId/episodes/:projectId",
          name: "account-episode-details",
          component: () => import("@/views/client/ClientEpisodeDetail.vue"),
          meta: {
            title: "Episode | Vysual",
          },
        },
        {
          path: "conversations/:conversationId?",
          name: "account-conversations",
          component: () => import("@/views/client/Conversations.vue"),
          meta: {
            title: "Conversations | Vysual",
          },
          // Route-level so it also runs when navigating between client pages
          // (the parent beforeEnter only fires on entering /accounts/:id).
          beforeEnter: async (to) => {
            const company = await useClientCompaniesStore().getCompany(to.params.id);
            if (!company?.conversationsEnabled) {
              logger.warn('[Router] Conversations not enabled for company', { companyId: to.params.id });
              return { name: 'account-assets', params: { id: to.params.id } };
            }
            const user = useUserAuthStore().user;
            if (user?.type === 'user') {
              const entry = (user.companies || []).find(
                (c) => typeof c === 'object' && c.companyId === to.params.id
              );
              if (entry?.permissionLevel === 'view-only') {
                return { name: 'account-assets', params: { id: to.params.id } };
              }
            }
            return true;
          },
        },
      ],
      beforeEnter: async (to, from, next) => {
        const clientCompaniesStore = useClientCompaniesStore();
        const viewModeStore = useViewModeStore();
        const userAuthStore = useUserAuthStore();
        const storageSubscriptionStore = useStorageSubscriptionStore();

        try {
          const urlCompanyId = to.params.id;
          
          // For admin/super users accessing client routes, ensure client store is initialized
          if (userAuthStore.user && ['admin', 'super'].includes(userAuthStore.user.type)) {
            if (!clientCompaniesStore.initialized) {
              logger.info('[Router] Initializing client companies store for admin user');
              await clientCompaniesStore.init(userAuthStore.user.id);
            }
          }
          
          // Check if user can access this company
          const company = await clientCompaniesStore.getCompany(urlCompanyId);
          
          if (!company) {
            logger.error('[Router] Company not found:', { companyId: urlCompanyId });
            next({
              name: 'admin-my-work',
              query: { error: 'company-not-found' }
            });
            return;
          }

          // For admin/super users, check if impersonation needs to be adjusted
          if (userAuthStore.user && ['admin', 'super'].includes(userAuthStore.user.type)) {
            const currentImpersonatedCompany = viewModeStore.impersonatedCompany;
            
            // If we're impersonating a different company than what's in the URL, switch impersonation
            if (viewModeStore.isImpersonating && currentImpersonatedCompany !== urlCompanyId) {
              logger.info('[Router] Switching impersonation due to URL mismatch', {
                currentCompany: currentImpersonatedCompany,
                urlCompany: urlCompanyId
              });
              
              try {
                // Handle impersonation state change directly
                const { updateTheme, resetClientStores, ensureAdminData, forceReloadClientStores } = useImpersonationService();

                // Ensure admin data is loaded
                await ensureAdminData();

                // Reset all client stores first
                await resetClientStores();

                // Stop current impersonation
                viewModeStore.stopImpersonation();

                // Start new impersonation
                viewModeStore.startImpersonation(urlCompanyId);

                // Apply company theme
                updateTheme(company);

                // Force reload all client stores for the new company
                await forceReloadClientStores(urlCompanyId);

                logger.info('[Router] Successfully switched impersonation');
              } catch (impersonationError) {
                logger.error('[Router] Failed to switch impersonation:', impersonationError);
                // Continue anyway - let the page handle the mismatch
              }
            }
            // If we're not impersonating but accessing a client route, start impersonation
            else if (!viewModeStore.isImpersonating) {
              logger.info('[Router] Starting impersonation for direct client route access', {
                urlCompany: urlCompanyId
              });
              
              try {
                // Handle impersonation start directly
                const { updateTheme, resetClientStores, ensureAdminData, forceReloadClientStores } = useImpersonationService();

                // Ensure admin data is loaded
                await ensureAdminData();

                // Reset any existing client stores first
                await resetClientStores();

                // Switch to client view mode
                viewModeStore.startImpersonation(urlCompanyId);

                // Apply company theme
                updateTheme(company);

                // Force reload all client stores for the new company
                await forceReloadClientStores(urlCompanyId);

                logger.info('[Router] Successfully started impersonation');
              } catch (impersonationError) {
                logger.error('[Router] Failed to start impersonation:', impersonationError);
                // Continue anyway - let the page handle the lack of impersonation
              }
            }
          }

          // Check vysualOnly restriction — applies to ALL users including impersonating admins
          if (company.vysualOnly && VYSUAL_ONLY_BLOCKED_ROUTES.includes(to.name)) {
            logger.warn('[Router] Vysual Only company - route blocked', {
              companyId: urlCompanyId,
              route: to.name
            });
            next({
              name: 'account-assets',
              params: { id: urlCompanyId }
            });
            return;
          }

          // Check client permission level (skip for admin/super users)
          if (userAuthStore.user && userAuthStore.user.type === 'user') {
            // First, verify the user has access to this company at all
            if (!hasCompanyAccess(userAuthStore.user, urlCompanyId)) {
              logger.warn('[Router] User does not have access to company', {
                userId: userAuthStore.user.id,
                companyId: urlCompanyId
              });
              const primaryCompanyId = getPrimaryCompanyId(userAuthStore.user);
              if (primaryCompanyId) {
                next({
                  name: 'account-assets',
                  params: { id: primaryCompanyId }
                });
              } else {
                next({ name: 'login' });
              }
              return;
            }

            // Then check permission level for the route
            const companies = userAuthStore.user.companies || [];
            const companyEntry = companies.find(c => {
              if (typeof c === 'string') return c === urlCompanyId;
              return c.companyId === urlCompanyId;
            });

            const permissionLevel = (companyEntry && typeof companyEntry === 'object')
              ? companyEntry.permissionLevel || 'administrator'
              : 'administrator';

            // The storage guard below redirects INTO account-company-details,
            // which view-only cannot open — bouncing it back to account-assets
            // re-triggers that guard, so the two loop forever. Let the
            // storage-blocked landing through; its tabs are gated separately.
            const isStorageLanding = to.name === 'account-company-details' &&
              (to.query.storageBlocked === 'true' || to.query.storageFull === 'true');

            const blocked = BLOCKED_ROUTES[permissionLevel] || [];
            if (!isStorageLanding && blocked.includes(to.name)) {
              logger.warn('[Router] Client permission denied for route', {
                userId: userAuthStore.user.id,
                permissionLevel,
                route: to.name,
                companyId: urlCompanyId
              });
              next({
                name: 'account-assets',
                params: { id: urlCompanyId }
              });
              return;
            }
          }

          // Check for storage access restrictions on asset-related routes
          const protectedRoutes = [
            'account-assets',
            'account-asset-detail',
            'account-upload',
            'account-sharing'
          ];

          if (protectedRoutes.includes(to.name)) {
            try {
              // Load storage subscription if not already loaded
              await storageSubscriptionStore.loadSubscription(urlCompanyId);

              // Check if access is blocked due to expired grace period
              const isBlocked = storageSubscriptionStore.isAccessBlocked(urlCompanyId);

              if (isBlocked) {
                logger.warn('[Router] Storage access blocked for company', {
                  companyId: urlCompanyId,
                  route: to.name
                });

                next({
                  name: 'account-company-details',
                  params: { id: urlCompanyId },
                  query: { storageBlocked: 'true' }
                });
                return;
              }

              // Block upload route when storage is at or above the limit
              if (to.name === 'account-upload') {
                await storageSubscriptionStore.loadUsage(urlCompanyId);
                const overLimit = storageSubscriptionStore.isOverLimit(urlCompanyId);

                if (overLimit) {
                  logger.warn('[Router] Upload blocked - storage at or above limit', {
                    companyId: urlCompanyId,
                    usagePercent: storageSubscriptionStore.usagePercentage(urlCompanyId)
                  });

                  next({
                    name: 'account-company-details',
                    params: { id: urlCompanyId },
                    query: { storageFull: 'true' }
                  });
                  return;
                }
              }
            } catch (storageError) {
              // Log but don't block access if we can't check storage
              logger.error('[Router] Failed to check storage access:', {
                error: storageError.message,
                companyId: urlCompanyId
              });
              // Continue to route - better to allow access than block incorrectly
            }
          }

          next();
        } catch (error) {
          logger.error('[Router] Error in route guard:', {
            error: error.message,
            stack: error.stack,
            urlCompanyId: to.params.id
          });
          next({
            name: 'admin-my-work',
            query: { error: 'route-load-failed' }
          });
        }
      }
    },
    // Agency portal — managing CLIENT companies. Distinct from the agency's own
    // company portal at /accounts/:agencyId, which is an ordinary client portal.
    // The agency doc ID and its company doc ID are the same value.
    {
      path: "/agency/:agencyId",
      children: [
        {
          path: "",
          redirect: to => ({ name: "agency-companies", params: to.params })
        },
        {
          path: "companies",
          name: "agency-companies",
          component: () => import("@/views/agency/AgencyCompanies.vue"),
          meta: { title: "Clients | Vysual", agencyPortal: true },
        },
        {
          path: "requests",
          name: "agency-requests",
          component: () => import("@/views/agency/AgencyRequests.vue"),
          meta: { title: "Requests | Vysual", agencyPortal: true },
        },
        {
          // The review opens as a modal over the queue rather than its own page.
          // Kept as a redirect so existing links still resolve.
          path: "requests/:projectId",
          name: "agency-request-review",
          redirect: to => ({
            name: "agency-requests",
            params: { agencyId: to.params.agencyId },
            query: { review: to.params.projectId },
          }),
        },
        {
          path: "users",
          name: "agency-users",
          component: () => import("@/views/agency/AgencyUsers.vue"),
          // Agency admins only: it lists who reaches which company, and exists
          // to change that.
          meta: { title: "Team | Vysual", agencyPortal: true, agencyAdmin: true },
        },
        {
          path: "settings",
          name: "agency-settings",
          component: () => import("@/views/agency/AgencySettings.vue"),
          // Agency admins only: the markup is set here.
          meta: { title: "Agency Settings | Vysual", agencyPortal: true, agencyAdmin: true },
        },
      ],
    },
    // Admin routes
    {
      path: "/nexus",
      children: [
        {
          // Bare /nexus matched nothing before; send admins to their landing page.
          path: "",
          redirect: () =>
            defaultUserRoute(useUserAuthStore().user) || { name: "admin-companies" },
        },
        {
          path: "projects/edit/:project",
          name: "admin-project-edit",
          component: () => import("@/views/admin/AdminEditProject.vue"),
          meta: {
            title: "Edit Project | vysual",
            permissionGroup: "Projects",
            parentRoute: "admin-projects",
          },
          // Fresh In-Review projects MUST go through the Review & Set Up wizard —
          // the editor is not a way to bypass it.
          beforeEnter: async (to) => {
            const proj = await loadProjectForGuard(to.params.project);
            if (proj && isReviewSetupEligible(proj)) {
              return { name: "admin-project-review", params: { project: to.params.project } };
            }
            return true;
          },
        },
        {
          path: "projects/:project/review",
          name: "admin-project-review",
          component: () => import("@/views/admin/ReviewSetupWizard.vue"),
          meta: {
            title: "Review & Set Up | vysual",
            permissionGroup: "Projects",
            parentRoute: "admin-projects",
          },
          // Only fresh In-Review, non-MSA/non-podcast projects use the wizard.
          // Ineligible (e.g. client-edit, already set up) → fall back to the editor.
          beforeEnter: async (to) => {
            const proj = await loadProjectForGuard(to.params.project);
            if (!proj) return { name: "admin-companies" };
            if (!isReviewSetupEligible(proj)) {
              return { name: "admin-project-edit", params: { project: to.params.project } };
            }
            return true;
          },
        },
        {
          path: "deliverables",
          name: "admin-deliverables",
          component: () => import("@/views/admin/AdminDeliverables.vue"),
          meta: {
            title: "Deliverables | vysual",
            permissionGroup: "Projects",
          },
        },
        {
          path: "my-work/:userId?",
          name: "admin-my-work",
          component: () => import("@/views/admin/AdminMyWork.vue"),
          meta: {
            title: "My Work | vysual",
          },
        },
        {
          path: "users",
          name: "admin-users",
          component: () => import("@/views/admin/AdminManagement.vue"),
          meta: {
            title: "Admin Management | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "companies",
          name: "admin-companies",
          component: () => import("@/views/admin/AdminCompanies.vue"),
          // Deliberately NO permissionGroup: the company page is the only door to
          // Projects and Podcasts now, so gating the roster would strand every admin
          // who holds those permissions but not Companies (e.g. the Project Manager
          // group). The company's sections are gated individually instead.
          meta: {
            title: "Companies | vysual",
          },
        },
        {
          path: "asset-audit",
          name: "admin-asset-audit",
          component: () => import("@/views/admin/AdminAssetAudit.vue"),
          meta: {
            title: "Asset Audit | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "transcription",
          name: "admin-transcription",
          component: () => import("@/views/admin/AdminTranscription.vue"),
          meta: {
            title: "Transcription | vysual",
            permissionGroup: "Tools",
            // Super-admin only — the transcription status/backlog callables assert super.
            requiresSuper: true,
          },
        },
        {
          path: "companies/create",
          name: "admin-companies-create",
          redirect: { name: "admin-companies" },
          meta: {
            title: "Create Company | vysual",
            permissionGroup: "Companies",
            parentRoute: "admin-companies",
          },
        },
        {
          // Parent record carries NO permission meta on purpose. vue-router merges the
          // meta of every matched record (children win), so a permissionGroup or
          // parentRoute here would silently leak onto the children below and undo
          // both the ungated Overview and the independently-grantable sections.
          path: "companies/details/:id",
          component: () => import("@/views/admin/AdminCompanyDetails.vue"),
          children: [
            {
              // No overview section: this route just picks the best landing section
              // for the company (projects > podcasts > contracts). The name lives
              // on this child rather than the parent so the existing
              // router.push({ name: 'admin-companies-details' }) calls keep working.
              // Ungated, like the companies list.
              path: "",
              name: "admin-companies-details",
              component: () =>
                import("@/components/admin/company/sections/CompanySectionRedirect.vue"),
              meta: { title: "Company | vysual" },
            },
            {
              // Name reused from the deleted top-level list route: keeps every stored
              // permission array and every meta.parentRoute: "admin-projects" valid.
              path: "projects",
              name: "admin-projects",
              component: () => import("@/views/admin/AdminProjects.vue"),
              meta: {
                title: "Company Projects | vysual",
                permissionGroup: "Projects",
              },
            },
            {
              path: "podcasts",
              name: "admin-podcasts",
              component: () => import("@/views/admin/AdminPodcasts.vue"),
              meta: {
                title: "Company Podcasts | vysual",
                permissionGroup: "Podcasts",
              },
            },
            {
              // The same board as /nexus/deliverables, locked to this company.
              // parentRoute points at that page so the existing grant covers it
              // — no new permission key to hand out, and getAllPermissionKeys
              // skips parentRoute records so it stays out of the drawer.
              path: "deliverables",
              name: "admin-company-deliverables",
              component: () => import("@/views/admin/AdminDeliverables.vue"),
              meta: {
                title: "Company Deliverables | vysual",
                permissionGroup: "Projects",
                parentRoute: "admin-deliverables",
              },
            },
            {
              // Was /nexus/assets/upload with a company picker; the company page
              // already knows which company you mean, so the picker is redundant
              // here. Name unchanged — permission grants key off it.
              path: "upload",
              name: "admin-asset-upload",
              component: () => import("@/views/admin/AdminAssetUpload.vue"),
              meta: {
                title: "Asset Upload | vysual",
                permissionGroup: "Tools",
              },
            },
            {
              path: "contracts",
              name: "admin-company-contracts",
              component: () =>
                import("@/components/admin/company/CompanyContractsTab.vue"),
              meta: {
                title: "Company Contracts | vysual",
                permissionGroup: "Companies",
              },
            },
            {
              path: "storage",
              name: "admin-company-storage",
              component: () =>
                import("@/components/admin/company/CompanyStorageTab.vue"),
              meta: {
                title: "Company Storage | vysual",
                permissionGroup: "Companies",
              },
            },
            {
              // Reuses the inbox scoped to this company; the parentRoute grant
              // covers it, same as company deliverables.
              path: "conversations",
              name: "admin-company-conversations",
              component: () => import("@/views/admin/AdminConversations.vue"),
              meta: {
                title: "Company Conversations | vysual",
                permissionGroup: "Conversations",
                parentRoute: "admin-conversations",
              },
            },
            {
              path: "users",
              name: "admin-company-users",
              component: () =>
                import("@/components/admin/company/CompanyUsersTab.vue"),
              meta: {
                title: "Company Users | vysual",
                permissionGroup: "Companies",
              },
            },
            {
              path: "agency",
              name: "admin-company-agency",
              component: () =>
                import("@/components/admin/company/CompanyAgencyTab.vue"),
              meta: {
                title: "Company Agency | vysual",
                permissionGroup: "Companies",
              },
            },
            {
              path: "activity",
              name: "admin-company-activity",
              component: () =>
                import("@/components/admin/company/CompanyActivityTimeline.vue"),
              meta: {
                title: "Company Activity | vysual",
                permissionGroup: "Companies",
              },
            },
            {
              path: "settings",
              name: "admin-company-settings",
              component: () =>
                import("@/components/admin/company/sections/CompanySettingsSection.vue"),
              meta: {
                title: "Company Settings | vysual",
                permissionGroup: "Companies",
              },
            },
          ],
        },
        {
          path: "podcasts/blueprint/:showId",
          name: "admin-blueprint-generator",
          component: () => import("@/views/admin/AdminBlueprintGenerator.vue"),
          meta: {
            title: "Blueprint Generator | vysual",
            permissionGroup: "Podcasts",
            parentRoute: "admin-podcasts",
          },
        },
        {
          path: "podcasts/:showId/discussion-guide",
          name: "admin-discussion-guide-editor",
          component: () => import("@/views/admin/AdminDiscussionGuideEditor.vue"),
          meta: {
            title: "Discussion Guide Template | vysual",
            permissionGroup: "Podcasts",
            parentRoute: "admin-show-episodes",
          },
        },
        {
          path: "podcasts/:showId/episodes/:project",
          name: "admin-episode-edit",
          component: () => import("@/views/admin/AdminEditProject.vue"),
          meta: {
            title: "Edit Episode | vysual",
            permissionGroup: "Podcasts",
            parentRoute: "admin-show-episodes",
          },
        },
        {
          path: "podcasts/:showId",
          name: "admin-show-episodes",
          component: () => import("@/views/admin/AdminShowEpisodes.vue"),
          meta: {
            title: "Show Episodes | vysual",
            permissionGroup: "Podcasts",
            parentRoute: "admin-podcasts",
          },
        },
        {
          path: "calendar",
          name: "admin-calendar",
          component: () => import("@/views/admin/AdminCalendar.vue"),
          meta: {
            title: "Calendar | vysual",
            permissionGroup: "Calendar",
          },
        },
        {
          path: "calendar/:id",
          name: "admin-calendar-event",
          component: () => import("@/views/admin/AdminCalendarEvent.vue"),
          meta: {
            title: "Calendar Event | vysual",
            permissionGroup: "Calendar",
            parentRoute: "admin-calendar",
          },
        },
        {
          path: "feedback",
          name: "admin-feedback",
          component: () => import("@/views/admin/AdminFeedback.vue"),
          meta: {
            title: "Issue Tracker | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          // Old Support Chat URL — Conversations replaced it.
          path: "support",
          redirect: { name: "admin-conversations" },
        },
        {
          path: "conversations",
          name: "admin-conversations",
          component: () => import("@/views/admin/AdminConversations.vue"),
          meta: {
            title: "Conversations | vysual",
            permissionGroup: "Conversations",
          },
        },
        {
          path: "user-access-report",
          name: "admin-user-access-report",
          component: () => import("@/views/admin/UserAccessReport.vue"),
          meta: {
            title: "User Audit | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "dgf-report",
          name: "admin-dgf-report",
          component: () => import("@/views/admin/DGFReport.vue"),
          meta: {
            title: "DGF Report | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "loa-report",
          name: "admin-loa-report",
          component: () => import("@/views/admin/LOAReport.vue"),
          meta: {
            title: "LOA Report | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "editor-stage-duration",
          name: "admin-editor-stage-duration",
          component: () => import("@/views/admin/EditorStageDurationReport.vue"),
          meta: {
            title: "Editor Stage Duration | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "msa-invoicing",
          name: "admin-msa-invoicing",
          component: () => import("@/views/admin/MsaInvoicingReport.vue"),
          meta: {
            title: "MSA Invoicing | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "podcast-publishing",
          name: "admin-podcast-publishing",
          component: () => import("@/views/admin/PodcastPublishSchedulingReport.vue"),
          meta: {
            title: "Podcast Publishing | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "email-delivery",
          name: "admin-email-delivery",
          component: () => import("@/views/admin/EmailDeliveryReport.vue"),
          meta: {
            title: "Email Delivery | vysual",
            permissionGroup: "Reports",
          },
        },
        {
          path: "delivery-risk",
          name: "admin-delivery-risk",
          component: () => import("@/views/admin/DeliveryRiskReport.vue"),
          meta: {
            title: "Delivery Risk | vysual",
            permissionGroup: "Reports",
          },
        },

        {
          path: "form-builder/:id?",
          name: "admin-form-builder",
          component: () => import("@/views/admin/AdminFormBuilder.vue"),
          meta: {
            title: "Form Builder | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "oauth-apps",
          name: "admin-oauth-apps",
          component: () => import("@/views/admin/AdminOAuthApps.vue"),
          meta: {
            title: "OAuth Apps | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "pricing-config",
          name: "admin-pricing-config",
          component: () => import("@/views/admin/AdminPricingConfig.vue"),
          meta: {
            title: "Pricing Config | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "scheduling-rules",
          name: "admin-scheduling-rules",
          component: () => import("@/views/admin/AdminSchedulingRules.vue"),
          meta: {
            title: "Scheduling Rules | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "automation-rules",
          name: "admin-automation-rules",
          component: () => import("@/views/admin/AdminAutomationRules.vue"),
          meta: {
            title: "Deliverable Rules | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "automation-rules/new",
          name: "admin-automation-rule-create",
          component: () => import("@/views/admin/AdminAutomationRuleBuilder.vue"),
          meta: {
            title: "Create Automation Rule | vysual",
            permissionGroup: "Configuration",
            parentRoute: "admin-automation-rules",
          },
        },
        {
          path: "automation-rules/:id",
          name: "admin-automation-rule-edit",
          component: () => import("@/views/admin/AdminAutomationRuleBuilder.vue"),
          meta: {
            title: "Edit Automation Rule | vysual",
            permissionGroup: "Configuration",
            parentRoute: "admin-automation-rules",
          },
        },
        {
          path: "automation-rules/work-pool",
          name: "admin-workpool-settings",
          component: () => import("@/views/admin/AdminWorkPoolSettings.vue"),
          meta: {
            title: "Work Pool Settings | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "dgf-config",
          name: "admin-dgf-config",
          component: () => import("@/views/admin/AdminDGFConfig.vue"),
          meta: {
            title: "DGF Report Configuration | vysual",
            permissionGroup: "Configuration",
          },
        },
        {
          path: "dgf-config/new",
          name: "admin-dgf-rule-create",
          component: () => import("@/views/admin/AdminDGFRuleBuilder.vue"),
          meta: {
            title: "Create DGF Rule | vysual",
            permissionGroup: "Configuration",
            parentRoute: "admin-dgf-config",
          },
        },
        {
          path: "dgf-config/:id",
          name: "admin-dgf-rule-edit",
          component: () => import("@/views/admin/AdminDGFRuleBuilder.vue"),
          meta: {
            title: "Edit DGF Rule | vysual",
            permissionGroup: "Configuration",
            parentRoute: "admin-dgf-config",
          },
        },
        {
          path: "profile",
          name: "admin-profile",
          component: () => import("@/views/ProfileView.vue"),
          meta: {
            title: "My Profile | vysual",
          },
        },
        {
          path: "profile/:userId",
          name: "admin-view-user-profile",
          component: () => import("@/views/ProfileView.vue"),
          meta: {
            title: "View User Profile | vysual",
          },
        },
        {
          // Compat shim for links already out in the wild. Stripe checkout return URLs
          // (functions/storage/stripeCheckout.js) and notification redirects point at
          // /nexus/companies/{id}?tab=storage — a path that never existed, with a ?tab=
          // param the page never read. Declared last; "companies/create" is static and
          // "companies/details/:id" is longer, so neither is shadowed by this.
          path: "companies/:id",
          redirect: (to) => ({
            // hasOwn, not a bare lookup: COMPANY_TAB_ROUTES is an object literal,
            // so `?tab=toString` would otherwise hand the router a function.
            name: Object.hasOwn(COMPANY_TAB_ROUTES, to.query.tab ?? "")
              ? COMPANY_TAB_ROUTES[to.query.tab]
              : "admin-companies-details",
            params: { id: to.params.id },
          }),
        },
      ],
    },
    // Permission Denied route (accessible to all authenticated users)
    {
      path: "/permission-denied",
      name: "permission-denied",
      component: () => import("@/views/PermissionDenied.vue"),
      meta: {
        title: "Access Denied | vysual",
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // The app's scroll container is the <main> element (App.vue), not
    // window — so Vue Router's `savedPosition` (captured from window) is
    // always 0. We track <main>.scrollTop ourselves to restore browser
    // back/forward correctly.
    //
    // Capture happens synchronously here, before the DOM swap, so we read
    // the *leaving* route's scroll. Restore happens inside rAF so the
    // *incoming* route is already mounted.
    const mainElNow = document.querySelector("main");
    if (mainElNow && from?.fullPath) {
      mainScrollPositions.set(from.fullPath, mainElNow.scrollTop);
    }

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        const mainEl = document.querySelector("main");

        if (savedPosition) {
          // Browser back/forward — restore from our tracked map; ignore
          // savedPosition.top (always 0 since window doesn't scroll).
          const top = mainScrollPositions.get(to.fullPath) ?? 0;
          if (mainEl) mainEl.scrollTop = top;
          resolve({ top });
          return;
        }
        if (to.hash) {
          resolve({ el: to.hash, behavior: "smooth" });
          return;
        }
        // Query-only changes on the same path (e.g. filter / pagination)
        // keep their scroll so the user isn't yanked to the top mid-task.
        if (to.path === from.path) {
          resolve(null);
          return;
        }
        if (mainEl) mainEl.scrollTop = 0;
        resolve({ top: 0 });
      });
    });
  },
});

// Hold the loader across navigation itself.
//
// The outgoing page releases its key on unmount and the incoming one only
// registers during setup, so without this there is a window where nothing is
// registered — the loader hides and reappears a moment later with a blank
// screen in between. Cleared on nextTick AFTER the new route renders, by which
// point that route has registered its own load if it has one.
router.beforeEach((to, from, next) => {
  setGlobalLoading('route', true);
  next();
});

router.afterEach(() => {
  nextTick(() => setGlobalLoading('route', false));
});

router.beforeEach(async (to, from, next) => {
  const userAuthStore = useUserAuthStore();
  const viewModeStore = useViewModeStore();

  // Set document title based on the route's meta title
  document.title = to.meta.title || "vysual";

  // Wait for auth initialization to complete
  if (!userAuthStore.initialized) {
    await userAuthStore.init();
  }

  // Also wait for loading to complete (Firebase auth state check)
  if (userAuthStore.isLoading) {
    // Wait for auth state to be determined
    await new Promise((resolve) => {
      const unwatch = userAuthStore.$subscribe((mutation, state) => {
        if (!state.isLoading) {
          unwatch();
          resolve();
        }
      });
    });
  }
  
  // Check if we're navigating to an admin route while impersonating
  if (viewModeStore.isImpersonating && to.path.startsWith('/nexus')) {
    logger.info('[Router] Navigating to admin route while impersonating - cleaning up', {
      from: from.path,
      to: to.path,
      impersonatedCompany: viewModeStore.impersonatedCompany
    });
    
    try {
      const impersonationService = useImpersonationService(router);
      
      // Reset all client stores first
      await impersonationService.resetClientStores();
      
      // Stop impersonation
      viewModeStore.stopImpersonation();
      
      // Reset theme
      impersonationService.updateTheme(null);
      
      logger.info('[Router] Impersonation cleanup completed');
    } catch (error) {
      logger.error('[Router] Failed to cleanup impersonation:', error);
    }
  }

  // Handle account switching for regular users (not admin impersonation)
  if (!viewModeStore.isImpersonating && userAuthStore.user.type === 'user') {
    try {
      const accountSwitchingService = useAccountSwitchingService();
      
      // Check if we're switching between different accounts
      if (accountSwitchingService.isSwitchingAccounts(from, to)) {
        const fromCompanyId = accountSwitchingService.getCompanyIdFromRoute(from);
        const toCompanyId = accountSwitchingService.getCompanyIdFromRoute(to);
        
        logger.info('[Router] Switching between accounts - cleaning up stores', {
          from: from.path,
          to: to.path,
          fromCompanyId,
          toCompanyId
        });
        
        await accountSwitchingService.switchAccount(fromCompanyId, toCompanyId);
      }
      // Check if we're leaving an account route (e.g., going to account selector)
      else if (accountSwitchingService.isLeavingAccount(from, to)) {
        const fromCompanyId = accountSwitchingService.getCompanyIdFromRoute(from);
        
        logger.info('[Router] Leaving account route - cleaning up stores', {
          from: from.path,
          to: to.path,
          fromCompanyId
        });
        
        await accountSwitchingService.prepareAccountSwitch();
      }
    } catch (error) {
      logger.error('[Router] Failed to handle account switching:', error);
      // Don't block navigation on account switching errors
    }
  }

  // Check if the route is public (no authentication required)
  const isPublicRoute = to.meta.public === true;

  if (!userAuthStore.user.id && !isPublicRoute) {
    if (to.name !== "login") {
      // Save the attempted URL
      if (to.path !== "/") {
        localStorage.setItem('redirectUrl', to.fullPath);
      }
      // Abort the current navigation and redirect to the login page
      next(false);
      router.replace({ name: "login" });
    } else {
      // Continue to the login page if the user is not authenticated
      next();
    }
  } else {
    // Agency portal: only that agency's own staff (or Vysual staff) get in.
    // Agency users are ordinary usertype 'user', so the /nexus gate below already
    // keeps them out of admin routes — this is the mirror of it.
    if (to.meta.agencyPortal) {
      const isStaff = userAuthStore.user.type === 'admin' || userAuthStore.user.type === 'super';
      if (!isStaff && userAuthStore.user.agencyId !== to.params.agencyId) {
        logger.warn('[Router] Blocked access to another agency portal', {
          userId: userAuthStore.user.id,
          userAgencyId: userAuthStore.user.agencyId,
          attemptedAgencyId: to.params.agencyId
        });
        next({ name: userAuthStore.user.agencyId ? 'agency-companies' : 'permission-denied',
               params: userAuthStore.user.agencyId ? { agencyId: userAuthStore.user.agencyId } : {} });
        return;
      }

      // Team and Settings run the agency. Hiding the nav items is not enough —
      // a scoped member who types the URL, or follows an old link, must land
      // somewhere they belong rather than on a screen they cannot use.
      if (to.meta.agencyAdmin && !isStaff && !userAuthStore.isAgencyAdmin) {
        logger.warn('[Router] Blocked agency admin route for a scoped member', {
          userId: userAuthStore.user.id,
          route: to.name
        });
        next({ name: 'agency-companies', params: { agencyId: to.params.agencyId } });
        return;
      }
    }

    // Check if user is trying to access admin routes
    if (to.path.startsWith('/nexus') && userAuthStore.user.type !== 'admin' && userAuthStore.user.type !== 'super') {
      logger.warn('[Router] Non-admin user attempted to access admin route', {
        userId: userAuthStore.user.id,
        userType: userAuthStore.user.type,
        attemptedPath: to.path
      });
      // Redirect to user's default page based on their company setup
      if (userAuthStore.user.agencyId) {
        // Agency staff belong to many companies by design — send them to the
        // agency portal rather than an account-selector listing their roster
        next({ name: "agency-companies", params: { agencyId: userAuthStore.user.agencyId } });
      } else if (userAuthStore.user.companies && userAuthStore.user.companies.length > 1) {
        // Multi-company user - always redirect to account selector
        next({ name: "account-selector" });
      } else if (userAuthStore.user.activeCompanyId) {
        // Single company user - redirect to their assets
        next({
          name: "account-assets",
          params: { id: userAuthStore.user.activeCompanyId }
        });
      } else {
        // User has no companies - redirect to login
        next({ name: "login" });
      }
      return;
    }

    // Permission check for admin routes (super admins bypass all checks)
    if (to.path.startsWith('/nexus') && userAuthStore.user.systemRole !== 'super') {
      // Super-only routes: a non-super (admin) user is never allowed here,
      // regardless of any granted permissionGroup.
      if (to.meta.requiresSuper) {
        logger.warn('[Router] Non-super user blocked from super-only route', {
          userId: userAuthStore.user.id,
          attemptedRoute: to.name,
        });
        next({ name: 'permission-denied' });
        return;
      }
      if (to.meta.permissionGroup) {
        const rawPerms = userAuthStore.user.permissions || [];
        const userPerms = migratePermissions(rawPerms);

        // Walk up the parentRoute chain — granting a parent permission
        // implicitly grants access to all descendant routes. Also check
        // the route's own name (in case someone has explicit permission
        // for a leaf route without the parent).
        const checked = [];
        let hasPermission = false;

        let keyToCheck = to.name;
        const visited = new Set();
        while (keyToCheck && !visited.has(keyToCheck)) {
          visited.add(keyToCheck);
          checked.push(keyToCheck);
          if (userPerms.includes(keyToCheck)) {
            hasPermission = true;
            break;
          }
          // Walk up: prefer the routes table directly to avoid resolve quirks on cold load.
          const routeRecord = router.getRoutes().find(r => r.name === keyToCheck);
          const nextParent = routeRecord?.meta?.parentRoute;
          if (!nextParent || visited.has(nextParent)) break;
          keyToCheck = nextParent;
        }

        if (!hasPermission) {
          logger.warn('[Router] Admin user lacks permission for route', {
            userId: userAuthStore.user.id,
            systemRole: userAuthStore.user.systemRole,
            attemptedRoute: to.name,
            checkedPermissions: checked,
            userPermissions: rawPerms
          });
          next({ name: 'permission-denied' });
          return;
        }
      }
    }

    // If the user is already authenticated, check if the redirect URL is set
    const redirectUrl = localStorage.getItem('redirectUrl');
    if (redirectUrl) {
      // Validate that the user has permission for the redirect URL
      const isAdminUrl = redirectUrl.startsWith('/nexus/');
      const isAdmin = isAdminUser(userAuthStore.user);

      // If user doesn't have permission for the saved URL, redirect to their default
      if (isAdminUrl && !isAdmin) {
        logger.warn('[Router] Saved redirect URL is admin-only, redirecting to user default', {
          userId: userAuthStore.user.id,
          userType: userAuthStore.user.type,
          attemptedRedirect: redirectUrl
        });
        localStorage.removeItem('redirectUrl');

        // Redirect based on user's company setup
        const fallback = defaultUserRoute(userAuthStore.user);
        next(fallback || { name: "login", query: { error: 'no-companies' } });
        return;
      }

      // Redirect to the saved URL if user has permission
      localStorage.removeItem('redirectUrl');
      next(redirectUrl);
    } else {
      // Handle default redirects based on user type when no saved URL
      const userType = userAuthStore.user.type;
      
      // If user is going to login page but is already authenticated, redirect to default
      if (to.name === "login") {
        if (userType === "admin" || userType === "super") {
          next(defaultUserRoute(userAuthStore.user) || { name: "admin-companies" });
        } else if (userType === "user") {
          const dest = defaultUserRoute(userAuthStore.user);
          // No companies - continue to login to show the error
          dest ? next(dest) : next();
        } else {
          next();
        }
      } else {
        // Handle root path redirect for authenticated users
        if (to.path === "/" && userAuthStore.user.id) {
          if (userType === "admin" || userType === "super") {
            next(defaultUserRoute(userAuthStore.user) || { name: "admin-companies" });
          } else if (userType === "user") {
            const dest = defaultUserRoute(userAuthStore.user);
            next(dest || { name: "login", query: { error: 'no-companies' } });
          } else {
            // Continue to the requested URL
            next();
          }
        } else {
          // Continue to the requested URL
          next();
        }
      }
    }
  }
});

// Handle chunk loading errors (stale deployments)
// When a new version is deployed, old chunk filenames become invalid
router.onError((error, to) => {
  setGlobalLoading('route', false);

  const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
                       error.message?.includes('Loading chunk') ||
                       error.message?.includes('Loading CSS chunk');

  if (isChunkError) {
    logger.warn('[Router] Chunk loading error detected, reloading page', {
      error: error.message,
      targetRoute: to?.fullPath
    });

    // Store the intended destination so we can navigate there after reload
    if (to?.fullPath) {
      sessionStorage.setItem('chunkErrorRedirect', to.fullPath);
    }

    // Force a hard reload to get the latest assets
    window.location.reload();
  }
});

// Check for chunk error redirect after page load
const chunkErrorRedirect = sessionStorage.getItem('chunkErrorRedirect');
if (chunkErrorRedirect) {
  sessionStorage.removeItem('chunkErrorRedirect');
  // The router will handle navigating to the correct route after initialization
  logger.info('[Router] Recovered from chunk loading error', { redirect: chunkErrorRedirect });
}

// Track user activity on route changes
let lastActivityUpdate = 0;
const ACTIVITY_THROTTLE_MS = 60000; // 1 minute throttle

router.afterEach((to, _from) => {
  // Only track activity for authenticated users on non-public routes
  const userAuthStore = useUserAuthStore();
  
  if (userAuthStore.user?.id && !to.meta.public) {
    const now = Date.now();
    
    // Throttle updates to avoid excessive Firestore writes
    if (now - lastActivityUpdate > ACTIVITY_THROTTLE_MS) {
      lastActivityUpdate = now;
      userService.updateLastActivity(userAuthStore.user.id);
    }
  }
});

export default router;
